import React, { useState, useEffect, useRef } from 'react';
import styles from './ScenarioInPlaceEdit.module.css';

interface ScenarioInPlaceEditProps {
  content: string;
  onCancel: () => void;
  onSave: (newContent: string) => void;
  isUserMessage: boolean;
  maxLength?: number;
}

const ScenarioInPlaceEdit: React.FC<ScenarioInPlaceEditProps> = ({
  content,
  onCancel,
  onSave,
  isUserMessage,
  maxLength
}) => {
  const [editedContent, setEditedContent] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the textarea when the component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
      textareaRef.current.selectionEnd = textareaRef.current.value.length;
      
      // Automatically resize the textarea to fit the content
      adjustTextareaHeight();
    }
  }, []);

  // Add click outside handler to cancel edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);

  // Handle textarea input change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (!maxLength || newValue.length <= maxLength) {
      setEditedContent(newValue);
      adjustTextareaHeight();
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Let the paste happen naturally, then adjust height
    setTimeout(() => {
      adjustTextareaHeight();
    }, 0);
  };

  // Adjust textarea height to fit content
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // Store the scroll position
      const scrollTop = textareaRef.current.scrollTop;
      
      // Reset height to auto and set to scrollHeight
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, window.innerHeight * 0.6);
      textareaRef.current.style.height = `${newHeight}px`;
      
      // Restore scroll position
      textareaRef.current.scrollTop = scrollTop;
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleSave = () => {
    if (!maxLength || editedContent.length <= maxLength) {
      onSave(editedContent);
    }
  };

  const getCharCountClass = () => {
    if (!maxLength) return '';
    const ratio = editedContent.length / maxLength;
    if (ratio >= 1) return styles.charCountError;
    if (ratio >= 0.9) return styles.charCountWarning;
    return '';
  };

  // Prevent click events from propagating to parent elements
  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      ref={containerRef} 
      className={styles.editContainer}
      onClick={handleContainerClick}
    >
      <textarea
        ref={textareaRef}
        className={styles.editTextarea}
        value={editedContent}
        onChange={handleChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        rows={1}
        onClick={(e) => e.stopPropagation()}
      />
      <div className={styles.editActions}>
        <div className={styles.editHints}>
          <span>Esc to cancel</span>
          <span>Ctrl+Enter to save</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {maxLength && (
            <span className={`${styles.charCount} ${getCharCountClass()}`}>
              {editedContent.length}/{maxLength}
            </span>
          )}
          <div className={styles.editButtons}>
            <button 
              className={`${styles.editButton} ${styles.cancelButton}`} 
              onClick={onCancel}
            >
              Cancel
            </button>
            <button 
              className={`${styles.editButton} ${styles.saveButton}`} 
              onClick={handleSave}
              disabled={maxLength ? editedContent.length > maxLength : false}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioInPlaceEdit; 